import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import slugify from 'slugify';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/zh-cn';
import utc from 'dayjs/plugin/utc';
import { sharedConfig } from '@/config/sharedConfig';
import { toZonedTime } from 'date-fns-tz';
import { TZDate } from '@date-fns/tz';
import { parse, isValid } from 'date-fns';
import type { CalendlyUserSlotAvailability } from '@/types/expert';

dayjs.extend(utc);
dayjs.extend(timezone);

const localeMap: { [key: string]: string } = {
  'de-DE': 'de',
  en: 'en',
  'zh-cn': 'zh-Hans',
};

const SUPPORTED_LOCALES = ['de-DE', 'zh-Hans', 'en'];

export const getDayjsLocale = (reduxLocale: string): string => {
  return localeMap[reduxLocale] || sharedConfig.defaultLocale;
};

export function formatDuration(duration: number): string {
  const minutes = Math.ceil(duration / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}min`;
  } else {
    return `${minutes}min`;
  }
}

export const formatTitleForUrl = (title: string): string => {
  return slugify(title, {
    lower: true,
    strict: true,
  });
};

export const formatDate = (date: string | Date, locale: string = 'de-DE') => {
  const dayjsLocale = getDayjsLocale(locale);
  return dayjs(date).locale(dayjsLocale).format('DD. MMM YY');
};
export const formatDateToNumericalDate = (date: string | Date, locale: string = 'de-DE') => {
  const dayjsLocale = getDayjsLocale(locale);
  return dayjs(date).locale(dayjsLocale).format('DD.MM.YY');
};

/**
 * Desired behaviour is such that we always display times in Berlin timezone to end users for now
 * */
export const formatDateTimeToBerlinTz = (date: string, locale: string = 'de-DE') => {
  const dayjsLocale = getDayjsLocale(locale);
  return dayjs(date)
    .tz(sharedConfig.defaultTimezone)
    .locale(dayjsLocale)
    .format('DD. MMM YY, HH:mm');
};

export const formatDateTime = (
  date: string | Date | null | undefined,
  locale: string = 'de-DE',
  format: string = 'DD. MMM YY, HH:mm',
) => {
  if (!date) return '-';
  try {
    const dayjsLocale = getDayjsLocale(locale);
    return dayjs(date).locale(dayjsLocale).format(format);
  } catch (_error) {
    return '-';
  }
};

export function formatDateToDe(date: string) {
  return new Date(date as string).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Formats a date to German format with time (dd.MM.yyyy - HH:mm)
 */
export function formatDateTimeToDe(date: string | Date | null | undefined): string {
  if (!date) return '-';
  return dayjs(date).format('DD.MM.YYYY - HH:mm');
}

/**
 * Desired behaviour is such that we always display times in Berlin timezone to end users for now
 * */
export const formatTimeToBerlinTz = (date: string, locale: string = 'de-DE') => {
  const dayjsLocale = getDayjsLocale(locale);
  return dayjs(date)
    .tz(sharedConfig.defaultTimezone) // Convert to Berlin timezone
    .locale(dayjsLocale)
    .format('HH:mm');
};

export const formatTime = (date: string, locale: string = 'de-DE') => {
  const dayjsLocale = getDayjsLocale(locale);
  return dayjs(date).locale(dayjsLocale).format('HH:mm');
};

export const parseTimeRange = (date: string, timeRange: string) => {
  const [startTime, endTime] = timeRange?.split('-') || [];
  const bookingStartTime = dayjs(`${date} ${startTime.trim()}`, 'YYYY-MM-DD HH:mm');
  const bookingEndTime = dayjs(`${date} ${endTime.trim()}`, 'YYYY-MM-DD HH:mm');

  return { bookingStartTime, bookingEndTime };
};

export const transformBookingTime = (bookingTime: string): string => {
  const berlinTimeZone = 'Europe/Berlin';
  const sessionDuration = sharedConfig.expertDurationMins;

  // Convert UTC booking time to Berlin time
  const bookingDateInBerlin = dayjs(bookingTime).tz(berlinTimeZone);

  // Format the date to "Tuesday 25. June 2024"
  const formattedDate = bookingDateInBerlin.format('dddd DD. MMMM YYYY');

  // Format the time to "12:00"
  const startTime = bookingDateInBerlin.format('HH:mm');

  // Calculate the end time (50 minutes later)
  const endTime = bookingDateInBerlin.add(parseInt(sessionDuration), 'minute').format('HH:mm');

  return `${formattedDate} at ${startTime} - ${endTime} (Germany/Berlin)`;
};

export const isLiveCourseActive = (timeRange?: string, date?: string): boolean => {
  if (!timeRange || !date) {
    return false;
  }

  const [startTime, endTime] = timeRange.split('-') || [];
  // Parse the date in UTC
  const courseDate = dayjs.utc(date).tz('Europe/Berlin');

  // Combine the courseDate with startTime and endTime in Berlin timezone
  const bookingStartTime = dayjs.tz(
    `${courseDate.format('YYYY-MM-DD')} ${startTime.trim()}`,
    'YYYY-MM-DD HH:mm',
    'Europe/Berlin',
  );

  const bookingEndTime = dayjs.tz(
    `${courseDate.format('YYYY-MM-DD')} ${endTime.trim()}`,
    'YYYY-MM-DD HH:mm',
    'Europe/Berlin',
  );

  return (
    dayjs().isAfter(bookingStartTime.subtract(30, 'minute')) &&
    dayjs().isBefore(bookingEndTime.add(30, 'minute'))
  );
};

export function throttle<T extends (...args: never[]) => void>(
  func: T,
  delay: number,
): {
  (...args: Parameters<T>): void;
  cancel: () => void;
} {
  let lastCall = 0;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const throttled = (...args: Parameters<T>): void => {
    const now = Date.now();
    const remaining = delay - (now - lastCall);

    // Clear any pending timeout
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    if (remaining <= 0 || remaining > delay) {
      // If enough time has passed, execute immediately
      lastCall = now;
      func(...args);
    } else {
      // Schedule for later - ensuring we don't stack up multiple timeouts
      timeoutId = setTimeout(() => {
        lastCall = Date.now();
        timeoutId = null;
        func(...args);
      }, remaining);
    }
  };

  // Add cancel method to clear any pending timeout
  throttled.cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return throttled;
}

export function debounce<A extends unknown[]>(
  func: (...args: A) => void,
  delay: number,
): (...args: A) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return (...args: A): void => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

/**
 * Get the user's locale, normalized to one of the supported full locales.
 * If the user's locale is not supported, it falls back to 'en'.
 * @returns {string} - A supported full locale key (e.g., 'en', 'de-DE', or 'zh-cn')
 */
export function getUserLocale() {
  const userLocale = navigator.language || sharedConfig.defaultLocale;

  // Normalize and fallback to supported locales
  return normalizeLocale(userLocale);
}

/**
 * Normalize the user's locale to match supported full locales.
 * If unsupported, fallback to 'en'.
 * @param {string} userLocale - The user's detected locale
 * @returns {string} - A supported full locale key
 */
export function normalizeLocale(userLocale: string) {
  // Exact match: return if the locale is supported
  if (SUPPORTED_LOCALES.includes(userLocale)) {
    return userLocale;
  }

  // Extract the base language (e.g., 'en' from 'en-US')
  const baseLanguage = userLocale.split('-')[0].toLowerCase();

  // Match by base language (e.g., 'en')
  const matchingLocale = SUPPORTED_LOCALES.find((locale) => locale.startsWith(baseLanguage));

  // Return the matching full locale or fallback to 'en'
  return matchingLocale || sharedConfig.defaultLocale;
}

/**
 * Extract the base language (e.g., 'en', 'de') from a full locale key.
 * @param {string} locale - The full locale key (e.g., 'en-US')
 * @returns {string} - The base language (e.g., 'en')
 */
export function getBaseLanguage(locale: string) {
  return locale.split('-')[0];
}

/**
 * Given a date of any timezone, return it normalized to midnight, in Berlin timezone
 * */
export function stripTimezoneToBerlinTz(date: Date) {
  return toZonedTime(
    new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())),
    sharedConfig.defaultTimezone,
  );
}

// Determine user's timezone dynamically
export const getUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (_err) {
    console.warn('Failed to determine user timezone. Falling back to UTC.');
    return 'UTC';
  }
};

/** Given a Date in any timezone, return the same date represented in Berlin timezone.
 *  This strips any related timezone offset. Useful to preserve eg day selection while
 *  ignoring time offset (such as during the booking workflow)
 *
 *  Input: Tue Jan 28 2025 00:00:00 GMT+0900 (Japan Standard Time)
 *  Output:  2025-01-28T01:00:00.000+01:00 (shown ISO string printing (UTC))
 *  */
export function toBerlinMidnight(date: Date): Date {
  const localMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  // Get the local timezone offset in minutes
  const localOffsetMinutes = localMidnight.getTimezoneOffset();
  // Convert the local midnight to UTC by subtracting the local offset
  const utcMidnight = new Date(localMidnight.getTime() - localOffsetMinutes * 60000);

  // Adjust the UTC midnight to Berlin timezone (Europe/Berlin)
  return getBerlinTzDate(utcMidnight);
}

export function getBerlinNow() {
  return getBerlinTzDate(new Date());
}

export function getBerlinTzDate(date: Date | string | number): Date {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  return new TZDate(dateObj, sharedConfig.defaultTimezone);
}

export function getSlotsForSelectedDate(
  availableSlots: CalendlyUserSlotAvailability[],
  selectedDate: Date | null,
  locale: string = sharedConfig.defaultLocale,
) {
  if (!selectedDate) return [];

  return availableSlots
    .filter((slot) => {
      const slotDate = new TZDate(slot.startTime, sharedConfig.defaultTimezone).toDateString();
      return new TZDate(selectedDate, sharedConfig.defaultTimezone).toDateString() === slotDate;
    })
    .sort(
      (a, b) =>
        new TZDate(a.startTime, sharedConfig.defaultTimezone).getTime() -
        new TZDate(b.startTime, sharedConfig.defaultTimezone).getTime(),
    ) // Sort by time
    .map((slot) => {
      const dateObj = new TZDate(slot.startTime, sharedConfig.defaultTimezone);
      return dateObj.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: sharedConfig.defaultTimezone,
      });
    });
}

export function disableUnavailableDates(
  date: Date,
  availableSlots: CalendlyUserSlotAvailability[],
) {
  const dateString = date.toDateString();
  const availableDates = availableSlots.map((slot) =>
    new TZDate(slot.startTime, sharedConfig.defaultTimezone).toDateString(),
  );
  return !availableDates.includes(dateString);
}

// Format selected date
export function formatSelectedDate(date: Date, locale: string = sharedConfig.defaultLocale) {
  return date.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: sharedConfig.defaultTimezone,
  });
}

/**
 * //For now the experts are all of a mock organization that has the key specialists.
 * //Until we get a client named specialists we can know if a user is an expert if
 * //they are part of this made up organization.
 * @param {string} organisationKey - The user'sorganization key
 * @returns {boolean} - Is the for experts only
 */
export function isExpertOrganization(organisationKey: string): boolean {
  return organisationKey.toLowerCase() === 'specialists';
}

export const getInputDateFormat = (dateString: string | undefined): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

/** Converts ISO date (YYYY-MM-DD) or ISO string to DD.MM.YYYY for DateInput display */
export const isoDateToDdMmYyyy = (dateString: string | undefined): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

/** Converts DD.MM.YYYY to YYYY-MM-DD for API */
export const ddMmYyyyToIsoDate = (value: string): string => {
  if (!value || value.length < 10) return '';
  const parts = value.split('.');
  if (parts.length !== 3) return '';
  const [dd, mm, yyyy] = parts;
  return `${yyyy}-${mm}-${dd}`;
};

/** Formats raw input (digits/dots) into DD.MM.YYYY pattern for date inputs */
export const formatDdMmYyyyInput = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  let d = digits;

  // Auto-pad day: digit 4–9 can never be the start of a valid two-digit day (40–99 don't exist)
  if (digits.length === 1 && /[4-9]/.test(digits[0])) {
    d = '0' + digits;
  }

  // Auto-pad month: month digit 2–9 can never start a valid two-digit month (20–99 don't exist)
  if (digits.length === 3 && /[2-9]/.test(digits[2])) {
    d = digits.slice(0, 2) + '0' + digits[2];
  }

  let formatted = '';
  for (let i = 0; i < d.length && i < 8; i++) {
    formatted += d[i];
    if (i === 1 || i === 3) formatted += '.';
  }
  return formatted;
};

/** Converts a Date to DD.MM.YYYY display string (uses local date, not UTC) */
export const dateToDdMmYyyy = (date: Date): string => {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

/** Converts a Date to YYYY-MM-DD for API (uses local date, not UTC) */
export const dateToIsoDate = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/** Parses DD.MM.YYYY string to Date, or null if invalid (uses date-fns) */
export const parseDdMmYyyyToDate = (value: string): Date | null => {
  if (!value || value.length < 10) return null;
  try {
    const parsed = parse(value, 'dd.MM.yyyy', new Date());
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const isUUID = (value: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
};

export const isDevOrStaging =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname.includes('staging'));

export const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
